import React, { FC, useState } from 'react'
import IconHeadBrain from '@/components/icons/IconHeadBrain/IconHeadBrain'
import IconHandHeart from '@/components/icons/IconHandHeart/IconHandHeart'
import IconShieldLinear from '@/components/icons/IconShieldLinear/IconShieldLinear'
import Button from '@/components/atoms/Button/ButtonV2'
import FilterWizardModal from '@/components/FilterWizardModal/FilterWizardModal'
import IconRightArrow from '@/components/IconRightArrow/IconRightArrow'
import { Department, getDepartmentText } from '@/domain/department'

const DEPARTMENT_TEXT = {
  [Department.Default]: {
    header: {
      title: 'Find The Perfect Treatment For You',
      description:
        "Answer a few questions and we'll match you with the best options."
    },
    steps: [
      {
        title: 'What Are You Struggling With?',
        description:
          'Providers specializing in the conditions and substances for which you are seeking help.'
      },
      {
        title: 'What Kind of Care Do You Need?',
        description:
          'Residential, outpatient, and other levels of care to meet your unique needs.'
      },
      {
        title: 'What Insurance Do You Have?',
        description:
          'Depending on your plan, insurance may cover up to 100% the cost of treatment.'
      }
    ],
    button: {
      text: 'Start Your Journey'
    }
  },
  [Department.Providers]: {
    header: {
      title: 'Treatment Center Wizard',
      description:
        "Answer a few questions and we’ll find the best options for your patients' needs"
    },
    steps: [
      {
        title: 'Start with a Condition',
        description:
          'Select from a comprehensive list of mental health and substance use disorders.'
      },
      {
        title: 'Select a Level of Care',
        description:
          'Find treatment options through residential, outpatient, and other levels of care.'
      },
      {
        title: 'Choose Payment Options',
        description:
          'Pick from insurance options including federal, state, and private insurance.'
      }
    ],
    button: {
      text: 'Start Your Search Here'
    }
  }
}

type HowItWorksProps = {
  department: string
}

const HowItWorks: FC<HowItWorksProps> = ({
  department = Department.Default
}) => {
  const [isFilterWizardModalVisible, setIsFilterWizardModalVisible] =
    useState(false)

  const { header, steps, button } = getDepartmentText({
    textValues: DEPARTMENT_TEXT,
    department
  })

  return (
    <>
      <FilterWizardModal
        isFilterWizardModalVisible={isFilterWizardModalVisible}
        onClose={() => setIsFilterWizardModalVisible(false)}
      />
      <div className="flex max-w-[1216px] flex-col items-start justify-center gap-10 self-stretch rounded-3xl bg-primary-25 p-8 md:gap-14 xl:p-14">
        <div className="flex flex-col gap-4 text-center md:text-left">
          <h3 className="font-FieldsVariable text-2xl font-medium text-primary-900 md:max-w-[300px]">
            {header.title}
          </h3>
          <p className="text-lg text-primary-700">{header.description}</p>
        </div>

        <div className="flex w-full flex-col xl:flex-row">
          <div className="flex w-full gap-6 xl:flex-col">
            <div className="flex flex-col items-center justify-center xl:w-full xl:flex-row">
              <div className="flex size-14 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary-100 p-px">
                <IconHeadBrain customCss="w-7 h-7 text-primary-800 stroke-[1.5px]" />
              </div>
              <div className="h-full w-0.5 bg-primary-100 xl:h-0.5 xl:w-full"></div>
            </div>
            <div className="flex flex-col gap-4 pb-14 xl:gap-6 xl:pb-0 xl:pr-14">
              <p className="font-FieldsVariable text-[26px] font-medium leading-8 text-primary-600 md:font-normal xl:text-[34px] xl:leading-[42px]">
                {steps[0].title}
              </p>
              <p className="text-primary-700">{steps[0].description}</p>
            </div>
          </div>
          <div className="flex w-full gap-6 xl:flex-col">
            <div className="flex flex-col items-center justify-center xl:w-full xl:flex-row">
              <div className="flex size-14 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary-100 p-px">
                <IconHandHeart
                  className="size-7 text-primary-800"
                  strokeWidth="1.5"
                />
              </div>
              <div className="h-full w-0.5 bg-primary-100 xl:h-0.5 xl:w-full "></div>
            </div>
            <div className="flex flex-col gap-4 pb-14 xl:gap-6 xl:pb-0 xl:pr-14">
              <p className="font-FieldsVariable text-[26px] font-medium leading-8 text-primary-600 md:font-normal xl:text-[34px] xl:leading-[42px]">
                {steps[1].title}
              </p>
              <p className="text-primary-700">{steps[1].description}</p>
            </div>
          </div>
          <div className="flex w-full gap-6 xl:flex-col">
            <div className="flex flex-col items-center justify-center xl:w-full xl:flex-row">
              <div className="flex size-14 shrink-0 flex-col items-center justify-center rounded-2xl bg-primary-100 p-px">
                <IconShieldLinear
                  className="size-7 text-primary-800"
                  strokeWidth="1.5"
                />
              </div>
              <div className="h-full w-0.5 xl:h-0.5 xl:w-full"></div>
            </div>
            <div className="flex flex-col gap-4 xl:gap-6 xl:pr-14">
              <p className="font-FieldsVariable text-[26px] font-medium leading-8 text-primary-600 md:font-normal xl:text-[34px] xl:leading-[42px]">
                {steps[2].title}
              </p>
              <p className="text-primary-700">{steps[2].description}</p>
            </div>
          </div>
        </div>
        <div className="w-full md:w-auto">
          <Button
            onClick={() => {
              setIsFilterWizardModalVisible(true)
            }}
            type="primary"
            className="w-full text-center md:w-auto md:text-left"
          >
            {button.text} <IconRightArrow />
          </Button>
        </div>
      </div>
    </>
  )
}

export default HowItWorks
